import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications, useUsers } from "@/http/api-hooks";
import { Notification, User } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Bell } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";

const PAGE_SIZE = 10;

const mentionIdRe = /@\{(\d+)\}/g;
function stripMentionIds(text: string, users: User[]): string {
  return text.replace(mentionIdRe, (_, id) => {
    const user = users.find(u => u.id === parseInt(id, 10));
    return user ? `@${user.name}` : `@${id}`;
  });
}

function initials(name: string): string {
  return name
    .split(" ")
    .map(w => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function NotificationItem({
  notification,
  onRead,
  users,
}: {
  notification: Notification;
  onRead: (id: number) => void;
  users: User[];
}) {
  const navigate = useNavigate();
  const isUnread = !notification.read_at;

  function handleClick() {
    if (isUnread) onRead(notification.id);
    const match = notification.link.match(/^\/experiments\/(\d+)/);
    if (match) mutate(`/api/experiments/${match[1]}/notes`);
    navigate(notification.link);
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        "w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors flex items-start gap-3",
        isUnread && "bg-muted/30",
      )}
    >
      {notification.actor && (
        <div className="flex-shrink-0 h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
          {initials(notification.actor.name)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm leading-snug", isUnread && "font-medium")}>{notification.title}</p>
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {stripMentionIds(notification.body, users)}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
        </p>
      </div>
      {isUnread && <span className="flex-shrink-0 mt-1.5 h-2 w-2 rounded-full bg-primary" />}
    </button>
  );
}

export function NotificationBell() {
  const [showUnread, setShowUnread] = useState(true);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { data, mutate } = useNotifications({ unreadOnly: showUnread, limit });
  const { trigger: markRead } = useMarkNotificationRead();
  const { trigger: markAllRead } = useMarkAllNotificationsRead();
  const { data: users = [] } = useUsers();

  const items = data?.items ?? [];
  const unreadCount = data?.unread_count ?? 0;
  const hasMore = data?.has_more ?? false;

  function handleToggle() {
    setShowUnread(prev => !prev);
    setLimit(PAGE_SIZE);
  }

  function handleLoadMore() {
    setLimit(prev => prev + PAGE_SIZE);
  }

  async function handleMarkAllRead() {
    await markAllRead();
    mutate();
  }

  const emptyMessage = showUnread ? "No unread notifications" : "No notifications";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 max-h-[520px] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">Notifications</DropdownMenuLabel>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggle}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showUnread ? "Show all" : "Unread only"}
            </button>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator className="m-0" />
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{emptyMessage}</p>
        ) : (
          <div>
            {items.map(n => (
              <NotificationItem key={n.id} notification={n} onRead={id => markRead(id)} users={users} />
            ))}
            {hasMore && (
              <>
                <DropdownMenuSeparator className="m-0" />
                <button
                  onClick={handleLoadMore}
                  className="w-full text-xs text-muted-foreground hover:text-foreground py-2.5 transition-colors"
                >
                  Load more
                </button>
              </>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
